// @flow
/* globals document, window */
import React, { Component, Fragment } from "react";
import { stringify, parse } from "querystring";
import debounce from "lodash/debounce";
import Paper from "elevate-ui/Paper";
import axios from "axios";
import moment from "moment";
import transform from "lodash/transform";
import filter from "lodash/filter";
import keys from "lodash/keys";
import { GlobalProvider } from "../common/GlobalContext";
import GlobalAlert from "../common/GlobalAlert";
import ListingGrid from "../common/ListingGrid";
import ListingGridFilters from "../common/ListingGridFilters";
import SearchControls from "./SearchControls";
import NoListings from "../common/NoListings";
import SaveSearch from "./SaveSearch";
import Ads from "../common/Ads";

import type {
  $LocalListingViewModel,
  $History,
  $ReactRouter,
  $OpenHouses,
} from "flow/types";

type $Props = {
  esEnabled: boolean,
  filters: Object,
  activeMLSFeedLabels: Array<Object>,
  activeMLSFeeds: Array<number>,
  advancedSearchFilterOrder: Object,
  alerts: Object,
  cities: Array<Object>,
  compliance: Object,
  favorites: Array<number>,
  openHouses: $OpenHouses,
  geojsonShapes: Array<Object>,
  globalGA: string,
  isMapEnabled?: boolean,
  listings: $LocalListingViewModel[],
  location: $ReactRouter,
  mapProvider: Object,
  markers?: Array<Object>,
  mlsFeeds: Array<Object>,
  pagination: Object,
  path: string,
  searchFormAction: string,
  siteUser: Object,
  sortName: string,
  sortNameOptions: Array<{ label: string, value: string }>,
  theme: Object, // theme from the API/site settings, not the react-jss theme
  title: string,
  history: $History,
  showAds: boolean,
  savedSearchName: string,
  savedSearchId: number | null,
};

type $State = {
  clientLoaded: boolean,
  listings: $LocalListingViewModel[],
  pagination: Object, // @todo
  listingSearchLoading: boolean,
  markers?: Array<Object>,
  savedSearchVisible: boolean,
  savedSearchName: string,
  savedSearchId: number | null,
  openHouses: $OpenHouses,
};

let MapComponent = () => <div />;
class ListingSearch extends Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);
    this.state = {
      clientLoaded: false,
      listings: props.listings,
      pagination: props.pagination,
      listingSearchLoading: false,
      markers: props.markers,
      savedSearchVisible: false,
      savedSearchName: props.savedSearchName,
      savedSearchId: props.savedSearchId,
      openHouses: props.openHouses,
    };
  }

  componentDidMount() {
    MapComponent = require("./ListingMap").default;
    this.setState({
      clientLoaded: true,
    });
  }

  componentDidUpdate(prevProps: Object) {
    if (prevProps.location.search !== this.props.location.search) {
      this.setState({ listingSearchLoading: true });
      return axios
        .get(`/api${this.props.searchFormAction}${this.props.location.search}`)
        .then(({ data: { markers, listings, pagination, openHouses } }) => {
          const newState = {
            markers: markers || [],
            listingSearchLoading: false,
            listings,
            pagination,
            openHouses,
          };
          this.setState(newState);
        })
        .catch(() => {
          this.setState({ listingSearchLoading: false });
        });
    }
  }

  queryFromQS() {
    return this.props.location && this.props.location.search
      ? parse(this.props.location.search.replace("?", ""))
      : {};
  }

  trackPageView() {
    if (window.ga && typeof window.ga === "function") {
      window.ga("set", "page", this.props.path);
      window.ga("send", "pageview");
    }

    if (
      this.props.globalGA &&
      window.gtag &&
      typeof window.gtag === "function"
    ) {
      window.gtag("config", this.props.globalGA, {
        page_path: this.props.path,
      });
    }
  }

  handleSaveToggle = () => {
    this.setState((state) => ({
      savedSearchVisible: !state.savedSearchVisible,
    }));
  };

  handleSearchAction(values: Object) {
    this.props.history.push(
      `${this.props.searchFormAction}?${stringify(values)}`
    );
    this.trackPageView();
  }

  debounceSearch = debounce((values: Object) => {
    this.handleSearchAction(values);
  }, 650);

  handleSearch(e: Event, values: Object, preventDebounce?: boolean) {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    if (preventDebounce) {
      this.handleSearchAction(this.transformDateValues(values));
    } else {
      this.debounceSearch(this.transformDateValues(values));
    }
  }

  transformDateValues(values: Object): Object {
    const dateKeys = filter(keys(values), (key) => values[key] instanceof Date);
    const dateFilters = transform(
      dateKeys,
      (memo, dateKey) => (memo[dateKey] = moment(values[dateKey]).format()),
      {}
    );
    return Object.assign({}, values, dateFilters);
  }

  handleClear(e: Event, values: Object, preventDebounce?: boolean) {
    this.setState({
      savedSearchName: "",
      savedSearchId: null,
    });
    this.handleSearch(e, values, preventDebounce);
  }

  renderSearch() {
    const {
      path,
      isMapEnabled,
      mapProvider,
      sortNameOptions,
      title,
      sortName,
      showAds,
    } = this.props;
    const {
      listings,
      listingSearchLoading,
      markers,
      savedSearchVisible,
      savedSearchName,
      savedSearchId,
    } = this.state;
    if (isMapEnabled && this.state.clientLoaded) {
      return (
        <Fragment>
          <Ads showAds={showAds} position="header" />
          <ListingGridFilters
            query={this.queryFromQS()}
            sortNameOptions={sortNameOptions}
            handleSearch={(e, values, preventDebounce) =>
              this.handleSearch(e, values, preventDebounce)
            }
            handleSaveToggle={this.handleSaveToggle}
            handleClear={(e, values, preventDebounce) =>
              this.handleClear(e, values, preventDebounce)
            }
            style={{ margin: "26px auto" }}
            isMapEnabled
            title={title}
            sortName={sortName}
          />
          <SaveSearch
            visible={savedSearchVisible}
            savedSearchName={savedSearchName}
            handleSaveToggle={this.handleSaveToggle}
            query={this.queryFromQS()}
            savedSearchId={savedSearchId}
          />
          <MapComponent
            markers={markers}
            defaultZoom={10}
            mapTypeId={mapProvider.tileLayers.street}
            scrollWheelZoom={false}
            listings={listings}
            query={this.queryFromQS()}
            handleSearch={(e, values, preventDebounce) =>
              this.handleSearch(e, values, preventDebounce)
            }
            listingSearchLoading={listingSearchLoading}
          />
          <Ads showAds={showAds} position="footer" />
        </Fragment>
      );
    } else if (isMapEnabled) {
      return (
        <div
          style={{
            width: "100%",
            maxWidth: "1172px",
            height: "620px",
            margin: "50px auto",
          }}
        >
          <Paper
            style={{
              height: "620px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "24px",
            }}
          >
            Loading Map, One Moment
          </Paper>
        </div>
      );
    } else {
      return (
        <Fragment>
          <Ads showAds={showAds} position="header" />
          <ListingGridFilters
            query={this.queryFromQS()}
            sortNameOptions={sortNameOptions}
            handleSearch={(e, values, preventDebounce) =>
              this.handleSearch(e, values, preventDebounce)
            }
            handleSaveToggle={this.handleSaveToggle}
            handleClear={(e, values, preventDebounce) =>
              this.handleClear(e, values, preventDebounce)
            }
            sortName={sortName}
          />
          <SaveSearch
            visible={savedSearchVisible}
            savedSearchName={savedSearchName}
            handleSaveToggle={this.handleSaveToggle}
            query={this.queryFromQS()}
            savedSearchId={savedSearchId}
          />
          <ListingGrid
            listings={listings}
            title={title}
            listingSearchLoading={listingSearchLoading}
            path={path}
            zeroContent={<NoListings />}
          />
          <Ads showAds={showAds} position="footer" />
        </Fragment>
      );
    }
  }

  render() {
    const {
      advancedSearchFilterOrder,
      alerts,
      compliance,
      favorites,
      mapProvider,
      siteUser,
      theme,
    } = this.props;
    const { pagination, listingSearchLoading, openHouses } = this.state;

    return (
      <GlobalProvider
        compliance={compliance}
        favorites={favorites}
        openHouses={openHouses}
        siteUser={siteUser}
        mapProvider={mapProvider}
        theme={theme}
      >
        <Fragment>
          <GlobalAlert alerts={alerts} />
          <SearchControls
            {...this.props}
            advancedSearchFilterOrder={advancedSearchFilterOrder}
            query={this.queryFromQS()}
            handleSearch={(e, values) => this.handleSearch(e, values)}
            pagination={pagination}
            handlePageNavigation={(e, values) =>
              this.handleSearch(e, values, true)
            }
          >
            {this.renderSearch()}
            {listingSearchLoading ? (
              <div
                style={{
                  position: "absolute",
                  height: "100%",
                  width: "100%",
                  backgroundColor: "#000",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  top: 0,
                  zIndex: 1500,
                  opacity: 0.4,
                }}
              />
            ) : null}
          </SearchControls>
        </Fragment>
      </GlobalProvider>
    );
  }
}

export default ListingSearch;
